'use client';

import { AddressDto, Auction, AuctionBuyEstimate, Item } from '@/api';
import { toStripeAmount } from '@/app/utils/stripe';
import { analytics } from '@/helpers/Analytics';
import { getBuyNowAmount } from '@/helpers/BuyNow';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import { NonLatinCharacterException } from '@/helpers/Exceptions';
import { useAlert } from '@/providers/AlertProvider';
import { buyExpressFn, getExpressEstimateFn } from '@/queries/auction';
import { invoiceApi } from '@/queries/invoice';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError, ClickResolveDetails, LineItem, ShippingRate, ExpressCheckoutPartialAddress, ChangeResolveDetails, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { useMutation } from '@tanstack/react-query';
import i18next from 'i18n';
import { useRef } from 'react';
import toast from 'react-hot-toast';

export function BuyNowButton({ setLoading, loading, item, auction, onVisible }: { item: Item, auction: Auction, loading: boolean, setLoading: (loading: boolean) => void, onVisible?: (visible: boolean) => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const lastAddress = useRef<ExpressCheckoutPartialAddress>();
    const lastEstimate = useRef<AuctionBuyEstimate>();
    const { show, hide } = useAlert();

    const buy = useMutation({
        mutationFn: buyExpressFn,
        onSuccess(data, variables) {
            show({
                text: i18next.t('alert-text.purchase.title'),
                description: i18next.t('alert-text.purchase.subtitle'),
                animation: 'SuccessAnimation'
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).dataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).dataLayer.push({
                    event: 'buy',
                    name: item.name,
                    amount: parseFloat(lastEstimate.current?.total_amount || '0.00'),
                    currency: 'USD'
                });
            }
            if (data.created_user_id) {
                analytics.identify(data.created_user_id, { email: variables.dto.email_address });
            }
            setLoading(false);
        },
        onError: async (error) => {
            const exception = await ErrorHandler.transformError(error as Request);
            if (exception instanceof NonLatinCharacterException) {
                toast.error(i18next.t('common:error-text.address-none-latin-character'));
            } else {
                toast.error(exception.message || '');
            }
            setLoading(false);
            hide();
        },
    });

    function handleError(error: StripeError) {
        toast.error(error.message || '');
        setLoading(false);
        hide();
    }

    const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
        if (!stripe || !elements || !lastEstimate.current || !event.shippingAddress) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }
        setLoading(true);
        show({
            text: i18next.t('buy-now.loading'),
            animation: 'LoadingAnimation'
        });
        const result = await invoiceApi.invoiceControllerClientSecretForExpressEstimate(lastEstimate.current.id);

        // Use the clientSecret and Elements instance to confirm the setup
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: result.secret,
            confirmParams: {
                return_url: window.location.href.split('?')[0]
            },
            redirect: 'if_required'
        });

        if (error) {
            event.paymentFailed({ reason: 'fail' });
            handleError(error);
            hide();
        } else {
            buy.mutate({
                dto: {
                    name: event.billingDetails?.name,
                    email_address: event.billingDetails?.email || '',
                    estimate_id: lastEstimate.current?.id,
                    payment_intent: paymentIntent.id,
                    address: {
                        ...event.shippingAddress.address,
                        line_1: event.shippingAddress.address.line1,
                        line_2: event.shippingAddress.address.line2 || undefined,
                        mode: AddressDto.ModeEnum.Shipping,
                        full_name: event.shippingAddress.name,
                        phone: event.billingDetails?.phone
                    }
                },
                auctionId: auction.id
            });
        }
    };

    const getEstimate = useMutation({
        mutationFn: getExpressEstimateFn
    });

    const isOnlyPickup = auction.fulfillment === Auction.FulfillmentEnum.Pickup;

    const getLineItems = (estimate?: AuctionBuyEstimate) => {
        const lineItems: LineItem[] = [];
        const shippingRates: ShippingRate[] = [];

        lineItems.push({ amount: toStripeAmount(getBuyNowAmount(auction)), name: item.name });

        if (isOnlyPickup) {
            auction.pickup_options?.forEach((option) => {
                shippingRates.push({
                    id: option,
                    displayName: i18next.t('estimate.delivery-method-pickup', { option }),
                    amount: 0
                });
            });
        } else if (estimate) {
            if (estimate.shipping_amount > '0.00') {
                shippingRates.push({
                    id: 'ship',
                    displayName: i18next.t('estimate.ship'),
                    amount: toStripeAmount(estimate.shipping_amount)
                });
            }
            auction.pickup_options?.forEach((option) => {
                shippingRates.push({
                    id: option,
                    displayName: i18next.t('estimate.delivery-method-pickup', { option }),
                    amount: 0
                });
            });
            if (shippingRates.length === 0) {
                shippingRates.push({
                    id: 'ship',
                    displayName: i18next.t('estimate.ship'),
                    amount: 0
                });
            }
        } else {
            shippingRates.push({
                id: 'ship',
                displayName: i18next.t('estimate.ship'),
                amount: 0
            });
        }

        lineItems.push({
            name: i18next.t('estimate.taxes'),
            amount: toStripeAmount(estimate?.tax_amount || '0.00')
        });

        lineItems.push({
            name: i18next.t('estimate.fees'),
            amount: toStripeAmount(estimate?.fee_amount || '0.00')
        });

        return { lineItems, shippingRates };
    };

    return (
        <ExpressCheckoutElement
            options={{ layout: { maxColumns: 1, maxRows: 1, overflow: 'auto' }, paymentMethodOrder: ['apple_pay', 'google_pay', 'link'] }}
            onShippingAddressChange={async (e) => {
                lastAddress.current = e.address;
                try {
                    const estimate = await getEstimate.mutateAsync({
                        auctionId: auction.id,
                        dto: {
                            address: {
                                line_1: '',
                                full_name: '',
                                ...e.address,

                                mode: AddressDto.ModeEnum.Shipping
                            }
                        }
                    });
                    const options: ChangeResolveDetails = {
                        ...getLineItems(estimate)
                    };
                    analytics.track('ViewedEstimate', { amount: estimate.total_amount, item: item.name });
                    lastEstimate.current = estimate;
                    elements?.update({ amount: toStripeAmount(estimate.total_amount) });
                    e.resolve(options);
                } catch (error) {
                    const exception = await ErrorHandler.transformError(error as Error);
                    if (exception instanceof NonLatinCharacterException) {
                        toast.error(i18next.t('common:error-text.address-none-latin-character'));
                    }
                    e.reject();
                }
            }}
            onShippingRateChange={async (e) => {
                if (!lastAddress.current) {
                    return;
                }
                const estimate = await getEstimate.mutateAsync({
                    auctionId: auction.id,
                    dto: {
                        address: {
                            line_1: '',
                            full_name: '',
                            ...lastAddress.current,
                            mode: AddressDto.ModeEnum.Shipping
                        },
                        pickup_option: e.shippingRate.id !== 'ship' ? e.shippingRate.id : undefined
                    }
                });
                analytics.track('ViewedEstimate', { amount: estimate.total_amount, item: item.name });
                lastEstimate.current = estimate;
                elements?.update({ amount: toStripeAmount(estimate.total_amount) });
                e.resolve({
                    ...getLineItems(estimate)
                });
            }}
            onReady={({ availablePaymentMethods }) => {
                if (availablePaymentMethods) {
                    onVisible?.(true);
                } else {
                    onVisible?.(false);
                }
            }}
            onCancel={() => {
                elements?.update({ amount: toStripeAmount(getBuyNowAmount(auction)) });
            }}
            onClick={(e) => {
                if (loading) {
                    throw new Error('loading');
                }
                const options: ClickResolveDetails = {
                    shippingAddressRequired: true,
                    billingAddressRequired: true,
                    emailRequired: true,
                    phoneNumberRequired: true,
                    ...getLineItems()
                };
                e.resolve(options);
            }}
            onConfirm={onConfirm} />
    );
}
